import mediaContract from "./media-contract.json";

type MediaEnvironment = keyof typeof mediaContract.environments;

export class MediaContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MediaContractError";
  }
}

export const parseHttpsOrigin = (value: string, name: string): string => {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new MediaContractError(`${name} must be a valid HTTPS origin`);
  }

  if (
    parsed.protocol !== "https:" ||
    parsed.username ||
    parsed.password ||
    parsed.port ||
    parsed.pathname !== "/" ||
    parsed.search ||
    parsed.hash ||
    value !== parsed.origin
  ) {
    throw new MediaContractError(
      `${name} must be an HTTPS origin with no port, path, query, credentials or trailing slash`,
    );
  }

  return parsed.origin;
};

const isMediaEnvironment = (value: string): value is MediaEnvironment =>
  Object.prototype.hasOwnProperty.call(mediaContract.environments, value);

export const validateMediaContract = ({
  environment,
  graphqlUrl,
  twicpicsBaseUrl,
}: {
  environment: string;
  graphqlUrl: string;
  twicpicsBaseUrl: string;
}) => {
  if (!isMediaEnvironment(environment)) {
    const displayEnvironment = JSON.stringify(environment);
    throw new MediaContractError(
      `REACT_APP_ENVIRONMENT ${displayEnvironment} has no reviewed media contract`,
    );
  }

  const expected = mediaContract.environments[environment];
  const actualOrigin = parseHttpsOrigin(
    twicpicsBaseUrl,
    "REACT_APP_TWICPICS_BASE_URL",
  );
  if (actualOrigin !== expected.userMediaOrigin) {
    throw new MediaContractError(
      `REACT_APP_TWICPICS_BASE_URL must be ${expected.userMediaOrigin} for ${environment}; received ${actualOrigin}`,
    );
  }
  if (graphqlUrl !== expected.graphqlUrl) {
    throw new MediaContractError(
      `REACT_APP_PLATFORM_BASE_URL must produce ${expected.graphqlUrl} for ${environment}; received ${graphqlUrl}`,
    );
  }

  return {
    schemaVersion: mediaContract.schemaVersion,
    environment,
    siteUrl: expected.siteUrl,
    graphqlUrl: expected.graphqlUrl,
    apiMediaOrigins: expected.apiMediaOrigins,
    userMediaOrigin: expected.userMediaOrigin,
    marketingMediaOrigin: mediaContract.marketing.mediaOrigin,
  };
};
